import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  GridApi,
  ColumnApi,
  IDatasource,
  IGetRowsParams,
  ColDef,
  RowClickedEvent,
  ColumnResizedEvent,
  RowNode,
} from 'ag-grid-community';
import { IioDataGrid } from './IioDataGrid';
import { ResourceListCellRenderer } from './ResourceListCellRenderer';
import { GridSelectionCellRenderer } from './GridSelectionCellRenderer';
import { GridSelectionHeader } from './GridSelectionHeader';
import { PaginationControls } from './PaginationControls';
import { RESOURCE_GRID_COLUMNS } from './resourceGridColumns';
import './ResourceGrid.scss';
import { useResourceViewerStore } from '../stores/resourceViewerStore';
import { useResourceSelectionStore } from '../stores/resourceSelectionStore';
import { ResourceGridToolbar, type GridStats } from './ResourceGridToolbar';
import { SelectAllButtons } from './SelectAllButtons';
import { AddTagControl, type BulkContext } from './AddTagControl';
import { CreateBatchButton } from './CreateBatchButton';
import { ReportsButton } from './reports/ReportsButton';
import { TagFilterBuilder, type TagFilter } from './TagFilterBuilder';
import { createGridAdapter } from '../services/createGridAdapter';
import { GridSortFilterBar, type FilterModel, type SortItem } from './GridSortFilterBar';
import { getPreference, resetGridPreferencesOnProjectChange, setPreference } from '../services/angular-bridge';
import { useProjectIdFromRoute } from '../hooks/useProjectIdFromRoute';
import { loadGridColumnsState, saveGridColumnsState } from '../utilities/gridColumnsState';
import { filterModelToSearchExpression } from '../utilities/columnFilters';
import type { ResourceDatasource, SearchExpression } from '../services/resourceDatasource';
import type { NormalizedRow } from '../types/resource';

// Angular DisplayMode enum + preference key: ListView=0, GridView=1, key "DisplayMode".
const DISPLAY_MODE_PREF = 'DisplayMode';
// Sort + filter models are already ag-grid-native shapes, so reuse Angular's own keys.
// Unlike column layout, they're cleared when the project changes.
const GRID_SORT_MODEL_PREF = 'GridColumnsSortModel';
const GRID_FILTER_MODEL_PREF = 'GridColumnsFilterModel';
const GRID_ROW_HEIGHT = 40;
// Leading pinned selection column — structural (fixed width, locked), so it is excluded
// from the persisted column state (otherwise a stale saved width overrides its colDef).
const SELECTION_COL_ID = 'selection';
type ColumnState = ReturnType<ColumnApi['getColumnState']>[number];

/**
 * ResourceGrid — shared grid wiring for the Browse, Collection, and Search pages.
 *
 * Adapts a ResourceDatasource to ag-Grid's infinite row model, opens the resource
 * viewer on row-click, and registers a GridAdapter into resourceViewerStore so the
 * viewer's prev/next can page through this grid. The datasource can change at runtime
 * (e.g. a new search query) — the grid is re-pointed and its cache purged.
 *
 * List view (default) renders one full-width media row per resource via
 * ResourceListCellRenderer. Grid view renders the full tabular columns (resourceGridColumns.ts)
 * with column order/visibility/pin/width persisted via ColumnApi. Sort/filter models are
 * set in grid view and still apply in list view, where GridSortFilterBar shows them.
 */

// Row height for the media list rows (ResourceRow .row min-height is 124px).
const LIST_ROW_HEIGHT = 132;
// Rows per page in the list view (matches Angular's visibleRowCount for the list grid).
const LIST_PAGE_SIZE = 20;

interface Props {
  datasource: ResourceDatasource;
  /** Initial view when no saved DisplayMode preference exists. */
  view?: 'list' | 'grid';
  /** Show each row's collection name (useful for multi-collection browse). */
  showCollection?: boolean;
  /** Render per-row selection checkboxes + a select-all toolbar (default true in list view). */
  selectable?: boolean;
  /** Enables bulk actions (Add Tag, …) in the selection toolbar when provided. */
  bulkContext?: BulkContext;
  /**
   * Enables the toolbar tag-filter (Show/Hide Filter button + TagFilterBuilder panel).
   * The page owns the filter and rebuilds its datasource on `onApply`. Omit to hide the
   * filter (e.g. batch review, whose filter is derived from the route, not editable).
   */
  tagFilter?: { projectId: string; filter: TagFilter | null; onApply: (f: TagFilter) => void };
  columnDefs?: ColDef[];
}

export function ResourceGrid({
  datasource,
  view: initialView = 'list',
  showCollection = true,
  selectable = true,
  bulkContext,
  tagFilter,
  columnDefs = RESOURCE_GRID_COLUMNS,
}: Props) {
  // Tag-filter panel visibility (open by default when a filter is already applied).
  const [showFilter, setShowFilter] = useState<boolean>(
    () => !!(tagFilter?.filter && tagFilter.filter.size > 0)
  );
  const openAt = useResourceViewerStore((s) => s.openAt);
  // Identity of the document open in the viewer, so the grid can highlight its row and
  // move the highlight as prev/next navigates. Kept in a ref for the (stable) rowClassRules
  // function; an effect redraws grid rows when it changes.
  const openKey = useResourceViewerStore((s) =>
    s.isOpen && s.config ? `${s.config.projectId}:${s.config.collectionId}:${s.config.resourceId}` : null
  );
  const openKeyRef = useRef<string | null>(null);
  openKeyRef.current = openKey;
  // Browse position of the open document, to keep its (highlighted) row scrolled into view.
  const browseIndex = useResourceViewerStore((s) => (s.isOpen ? s.browseIndex : -1));
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Stable identity (empty deps) so it doesn't churn the grid; reads the ref each eval.
  const rowClassRules = useMemo(
    () => ({
      'resource-open': (p: { data?: NormalizedRow }) =>
        !!p.data && `${p.data.projectId}:${p.data.collectionId}:${p.data.id}` === openKeyRef.current,
    }),
    []
  );
  const setGridAdapter = useResourceViewerStore((s) => s.setGridAdapter);
  const setTotalRows = useResourceViewerStore((s) => s.setTotalRows);
  const clearSelection = useResourceSelectionStore((s) => s.clear);

  // Display mode is user-persisted (Angular DisplayMode preference) and wins over the
  // initial prop; the toggle writes it back so it survives navigation/reload.
  const [view, setView] = useState<'list' | 'grid'>(() =>
    getPreference<number>(DISPLAY_MODE_PREF) === 1 ? 'grid' : initialView
  );
  const changeView = useCallback((v: 'list' | 'grid') => {
    setView(v);
    setPreference(DISPLAY_MODE_PREF, v === 'grid' ? 1 : 0);
  }, []);

  const [stats, setStats] = useState<GridStats | null>(null);
  const totalRef = useRef(0);
  const apiRef = useRef<GridApi | null>(null);
  // A ref, not state: persistColumnState stays referentially stable and doesn't churn the
  // column defs. The column chooser lives in the grid header and reads the api from its own
  // header params.
  const columnApiRef = useRef<ColumnApi | null>(null);
  // The datasource identity currently applied to the grid, so we set it exactly once
  // per change (avoids a duplicate initial fetch from onGridReady + effect).
  const appliedRef = useRef<IDatasource | null>(null);

  // Saved sort/filter, first dropped if they belong to another project. Grid view hands
  // them to ag-Grid; list view (no such columns) fetches with them via the ref.
  const projectId = useProjectIdFromRoute();
  const [models, setModels] = useState(() => {
    resetGridPreferencesOnProjectChange(projectId);
    return {
      sort: getPreference<SortItem[]>(GRID_SORT_MODEL_PREF) ?? [],
      filter: getPreference<FilterModel>(GRID_FILTER_MODEL_PREF) ?? {},
    };
  });
  const modelsRef = useRef(models);
  modelsRef.current = models;
  const viewRef = useRef(view);
  viewRef.current = view;

  // The active column filters as a server SearchExpression, for the bulk-action context
  // ('all'-mode Add Tag / Batches / Reports).
  const columnFilters = useMemo<SearchExpression | undefined>(
    () => filterModelToSearchExpression(models.filter),
    [models.filter]
  );

  const agDatasource = useMemo<IDatasource>(
    () => ({
      getRows: (params: IGetRowsParams) => {
        const limit = params.endRow - params.startRow;
        const page0 = Math.floor(params.startRow / limit);
        const { sortModel, filterModel } = viewRef.current === 'grid'
          ? params
          : { sortModel: modelsRef.current.sort, filterModel: modelsRef.current.filter };
        const sort = sortModel?.[0];
        datasource
          .fetchPage({
            page0,
            limit,
            sortBy: sort?.colId ?? null,
            sortDirection: sort ? sort.sort.toUpperCase() : null,
            columnFilters: filterModelToSearchExpression(filterModel),
          })
          .then((page) => {
            totalRef.current = page.totalRows;
            setTotalRows(page.totalRows);
            setStats({ rows: page.totalRows, pages: page.totalPageCount, size: page.totalFileSize });
            params.successCallback(page.rows, page.totalRows);
          })
          .catch(() => params.failCallback());
      },
    }),
    [datasource, setTotalRows]
  );

  const refresh = useCallback(() => apiRef.current?.purgeInfiniteCache(), []);

  // Grid (tabular) view highlights the open row via rowClassRules; ag-Grid only
  // re-evaluates them on redraw. Redraw ONLY the previously- and newly-open rows (not the
  // whole grid) so the highlight moves without the full-grid re-render that flickered the
  // scrollbars/rows. List view highlights through the cell renderer, no redraw needed.
  const prevOpenIndexRef = useRef(-1);
  useEffect(() => {
    if (view !== 'grid') { prevOpenIndexRef.current = browseIndex; return; }
    const api = apiRef.current;
    if (!api) { prevOpenIndexRef.current = browseIndex; return; }
    const nodes: RowNode[] = [];
    const add = (i: number) => { const n = i >= 0 ? api.getDisplayedRowAtIndex(i) : null; if (n) nodes.push(n); };
    add(prevOpenIndexRef.current);
    if (browseIndex !== prevOpenIndexRef.current) add(browseIndex);
    prevOpenIndexRef.current = browseIndex;
    if (nodes.length) api.redrawRows({ rowNodes: nodes });
  }, [openKey, view, browseIndex]);

  // Keep the open document's row scrolled into view as prev/next moves the highlight.
  // ensureIndexVisible changes the page (list) and scrolls the grid's internal viewport
  // (grid view). In list view the grid is sized to a full page with no internal scrollbar,
  // so the *browser* scrolls instead — ag-Grid can't do that, so pull the highlighted row
  // into the viewport ourselves. Edge-pinning: leave the row alone while it moves within
  // view, then pin it to whichever edge it reaches so the list follows it a row at a time
  // (top edge is below the fixed nav + page control). A page change lands above/below the
  // view, so this naturally shows the new page's first row at top / last row at bottom.
  // Scoped under .ag-row so a pager aria-current can't match.
  useEffect(() => {
    if (browseIndex < 0) return;
    apiRef.current?.ensureIndexVisible(browseIndex);

    let raf = 0;
    let cancelled = false;
    let tries = 0;
    const run = () => {
      if (cancelled) return;
      const root = wrapperRef.current;
      const marker = root?.querySelector('.ag-row.resource-open, .ag-row [aria-current="true"]');
      const rowEl = (marker?.closest('.ag-row') ?? marker) as HTMLElement | null;
      // The page swap + highlight can take a frame or two to render; retry until the
      // highlighted row exists so a backward page change measures the new (last) row,
      // not the stale one.
      if (!rowEl) { if (tries++ < 5) raf = requestAnimationFrame(run); return; }
      if (view !== 'list') { rowEl.scrollIntoView({ block: 'nearest', inline: 'nearest' }); return; }
      // Fixed 70px top nav + the page control's measured height (only when a pager shows),
      // so neither hides the row.
      const NAV_HEIGHT = 70;
      const pager = root?.querySelector('.pagination');
      const topOffset = NAV_HEIGHT + (pager ? pager.getBoundingClientRect().height + 16 : 0);
      const rect = rowEl.getBoundingClientRect();
      if (rect.top < topOffset) {
        window.scrollBy({ top: rect.top - topOffset });        // pin to top edge
      } else if (rect.bottom > window.innerHeight) {
        window.scrollBy({ top: rect.bottom - window.innerHeight }); // pin to bottom edge
      }
    };
    raf = requestAnimationFrame(run);
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [browseIndex, view]);

  const applyDatasource = useCallback((api: GridApi, ds: IDatasource) => {
    api.setDatasource(ds);
    appliedRef.current = ds;
  }, []);

  const onGridReady = useCallback(
    (api: GridApi, colApi: ColumnApi) => {
      apiRef.current = api;
      setGridAdapter(createGridAdapter(api, () => totalRef.current));
      columnApiRef.current = colApi;
      // Restore column/sort/filter state BEFORE the first fetch so it's applied in one
      // pass (setDatasource picks up the sort/filter models rather than refetching).
      if (view === 'grid') {
        const savedCols = loadGridColumnsState<ColumnState>();
        if (savedCols) {
          // setColumnState hides and unpins any column absent from the state, so keep the
          // selection column's own (colDef) state in front of the saved columns.
          const selectionState = colApi.getColumnState().filter((c) => c.colId === SELECTION_COL_ID);
          colApi.setColumnState([...selectionState, ...savedCols.filter((c) => c.colId !== SELECTION_COL_ID)]);
        }
        api.setSortModel(modelsRef.current.sort);
        api.setFilterModel(modelsRef.current.filter);
      }
      applyDatasource(api, agDatasource);
    },
    [agDatasource, applyDatasource, setGridAdapter, view]
  );

  // Persist column order/visibility/pin/width whenever the user changes them, whether
  // via the chooser or directly on the grid (drag to move/resize/pin a header). Reads
  // the api via ref so this callback is stable (headerComponentParams must not churn).
  const persistColumnState = useCallback(() => {
    const colApi = columnApiRef.current;
    if (colApi) {
      saveGridColumnsState(colApi.getColumnState().filter((c) => c.colId !== SELECTION_COL_ID));
    }
  }, []);
  const onColumnMoved = useCallback(() => persistColumnState(), [persistColumnState]);
  const onColumnVisible = useCallback(() => persistColumnState(), [persistColumnState]);
  const onColumnPinned = useCallback(() => persistColumnState(), [persistColumnState]);
  const onColumnResized = useCallback(
    (e: ColumnResizedEvent) => { if (e.finished) persistColumnState(); },
    [persistColumnState]
  );

  // Server-side sort/filter. Grid view: ag-Grid purges + refetches on change and we
  // persist. List view (GridSortFilterBar): we persist and purge ourselves.
  const saveSort = useCallback((sort: SortItem[]) => {
    setPreference(GRID_SORT_MODEL_PREF, sort);
    setModels((m) => ({ ...m, sort }));
  }, []);
  const saveFilter = useCallback((filter: FilterModel) => {
    setPreference(GRID_FILTER_MODEL_PREF, filter);
    setModels((m) => ({ ...m, filter }));
    // The result set changed, so a prior selection may reference now-hidden rows (and
    // 'all' would silently re-scope) — drop it, mirroring Angular's deselect-on-refilter.
    clearSelection();
  }, [clearSelection]);
  const onSortChanged = useCallback(
    () => saveSort((apiRef.current?.getSortModel() ?? []) as SortItem[]),
    [saveSort]
  );
  const onFilterChanged = useCallback(
    () => saveFilter(apiRef.current?.getFilterModel() ?? {}),
    [saveFilter]
  );
  const onListSortChange = useCallback((sort: SortItem[]) => {
    modelsRef.current = { ...modelsRef.current, sort };
    saveSort(sort);
    apiRef.current?.purgeInfiniteCache();
  }, [saveSort]);
  const onListFilterChange = useCallback((filter: FilterModel) => {
    modelsRef.current = { ...modelsRef.current, filter };
    saveFilter(filter);
    apiRef.current?.purgeInfiniteCache();
  }, [saveFilter]);

  // Re-point the grid when the datasource changes after the grid is ready, and drop
  // any selection (mirrors the Angular grid's deselectAll() on datasource change).
  useEffect(() => {
    if (apiRef.current && appliedRef.current !== agDatasource) {
      applyDatasource(apiRef.current, agDatasource);
      clearSelection();
    }
  }, [agDatasource, applyDatasource, clearSelection]);

  const onRowClicked = useCallback(
    (e: RowClickedEvent) => {
      if (!e.data) return;
      openAt(e.rowIndex ?? 0, e.data as NormalizedRow);
    },
    [openAt]
  );

  // Drop the adapter + selection when leaving so the viewer never drives a dead grid
  // and selection doesn't leak into the next grid.
  useEffect(() => () => { setGridAdapter(null); clearSelection(); }, [setGridAdapter, clearSelection]);

  // Pagination: ag-Grid owns paging (built-in panel suppressed); we mirror its state
  // into PaginationControls rendered top + bottom, matching the Angular uib-pagination.
  const [pageInfo, setPageInfo] = useState({ current: 0, total: 0 });
  // Rows actually rendered on the current page (the last page is usually partial).
  // The list grid is sized to exactly this, so it never scrolls internally — the page
  // scrolls instead. Mirrors Angular's `element.height(rowHeight * count + 5)`.
  const [rowsOnPage, setRowsOnPage] = useState(LIST_PAGE_SIZE);
  const onPaginationChanged = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const current = api.paginationGetCurrentPage();
    setPageInfo({ current, total: api.paginationGetTotalPages() });
    const remaining = api.paginationGetRowCount() - current * LIST_PAGE_SIZE;
    setRowsOnPage(Math.max(1, Math.min(LIST_PAGE_SIZE, remaining)));
  }, []);
  const goToPage = useCallback((p: number) => { apiRef.current?.paginationGoToPage(p); }, []);

  // List view: a single full-width media-row column, no header.
  const listColumnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: '',
        colId: 'resource',
        flex: 1,
        sortable: false,
        cellRendererFramework: ResourceListCellRenderer as unknown as React.ComponentType,
        cellRendererParams: { showCollection, selectable },
      },
    ],
    [showCollection, selectable]
  );

  // Grid view: prepend a pinned selection column whose header hosts select-all + the
  // in-grid column chooser, and whose cells render per-row checkboxes. Memoized (with a
  // stable persistColumnState) so the column defs never churn and reset the grid.
  const gridColumnDefs = useMemo<ColDef[]>(() => {
    if (!selectable) return columnDefs;
    const selectionCol: ColDef = {
      colId: SELECTION_COL_ID,
      headerName: '',
      pinned: 'left',
      lockPosition: true,
      lockPinned: true,
      suppressMovable: true,
      sortable: false,
      resizable: false,
      width: 120,
      headerComponentFramework: GridSelectionHeader as unknown as React.ComponentType,
      headerComponentParams: { columnDefs, onColumnsChange: persistColumnState },
      cellRendererFramework: GridSelectionCellRenderer as unknown as React.ComponentType,
    };
    return [selectionCol, ...columnDefs];
  }, [selectable, columnDefs, persistColumnState]);

  // Fold the grid's live column filters into the bulk-action context so an 'all'-mode
  // Add Tag / Create Batches / Report matches exactly what the filtered grid shows.
  const effectiveBulk = useMemo(
    () => (bulkContext ? { ...bulkContext, columnFilters: columnFilters ?? bulkContext.columnFilters ?? null } : undefined),
    [bulkContext, columnFilters]
  );

  const isList = view === 'list';
  const rowHeight = isList ? LIST_ROW_HEIGHT : GRID_ROW_HEIGHT;
  const headerHeight = isList ? 0 : 32;
  // List view paginates (media rows, custom controls); grid view is infinite-scroll.
  const pager = isList && pageInfo.total > 1 && (
    <PaginationControls currentPage={pageInfo.current} totalPages={pageInfo.total} onGoTo={goToPage} />
  );

  return (
    <div ref={wrapperRef} className={`resource-list-grid view-${view}`}>
      {/* Row 1: stats (left) + Add Tag (right) — Angular resource_filter.html top row. */}
      <ResourceGridToolbar stats={stats}>
        {selectable && effectiveBulk?.canManageTags && <AddTagControl bulk={effectiveBulk} />}
      </ResourceGridToolbar>
      {/* Row 2: button strip in Angular order — refresh, reports, batches, view, columns, select-all. */}
      <div className="resource-grid-button-row">
        <button type="button" className="btn btn-default" onClick={refresh} title="Refresh">
          <i className="fas fa-sync-alt" />
        </button>
        {tagFilter && (
          <button type="button" className="btn btn-default" onClick={() => setShowFilter((s) => !s)} title="Tag Filter">
            <i className="fas fa-tags" /> {showFilter ? 'Hide' : 'Show'} Filter
            {(tagFilter.filter?.size ?? 0) > 0 && (
              <span className="badge" style={{ marginLeft: 6 }}>{tagFilter.filter!.size}</span>
            )}
          </button>
        )}
        {effectiveBulk?.canManageReports && <ReportsButton bulk={effectiveBulk} />}
        {effectiveBulk?.canCreateBatches && <CreateBatchButton bulk={effectiveBulk} />}
        <div className="btn-group" role="group">
          <button
            type="button"
            className={`btn btn-default${isList ? ' active' : ''}`}
            onClick={() => changeView('list')}
            title="List View"
            aria-pressed={isList}
          >
            <i className="fas fa-list" />
          </button>
          <button
            type="button"
            className={`btn btn-default${!isList ? ' active' : ''}`}
            onClick={() => changeView('grid')}
            title="Grid View"
            aria-pressed={!isList}
          >
            <i className="fas fa-th" />
          </button>
        </div>
        {selectable && <SelectAllButtons />}
      </div>
      {tagFilter && showFilter && (
        <TagFilterBuilder
          projectId={tagFilter.projectId}
          appliedFilter={tagFilter.filter}
          onApply={tagFilter.onApply}
        />
      )}
      {isList && (
        <GridSortFilterBar
          columnDefs={columnDefs}
          sortModel={models.sort}
          filterModel={models.filter}
          onSortChange={onListSortChange}
          onFilterChange={onListFilterChange}
        />
      )}
      {pager}
      {/* Remount on view change so ag-Grid picks up the new column set + row height cleanly;
          onGridReady re-applies the datasource + adapter. */}
      <IioDataGrid
        key={view}
        columnDefs={isList ? listColumnDefs : gridColumnDefs}
        // Grid view: default each column to a server-side text filter (columns that set
        // filter:false in the module opt out) and expose the Enterprise general menu tab
        // (autosize/reset) alongside the filter tab, so every column shows the header menu.
        // List view keeps its bare single column.
        defaultColDef={isList ? { cellClass: '' } : { filter: 'agTextColumnFilter', menuTabs: ['generalMenuTab', 'filterMenuTab'] }}
        rowHeight={rowHeight}
        headerHeight={headerHeight}
        // List view: exactly as tall as the rows on this page (+5 like Angular) so the
        // only scrollbar is the window's. Grid view keeps its fixed viewport height.
        height={isList ? rowHeight * rowsOnPage + 5 : rowHeight * LIST_PAGE_SIZE + headerHeight}
        // Single full-width column — nothing to scroll sideways, and reserving space for
        // a horizontal scrollbar is what forces the vertical one to appear.
        suppressHorizontalScroll={isList}
        suppressAutoSizeColumns={!isList}
        cacheBlockSize={LIST_PAGE_SIZE}
        onGridReady={onGridReady}
        onRowClicked={onRowClicked}
        {...(isList
          ? { pagination: true, suppressPaginationPanel: true, paginationPageSize: LIST_PAGE_SIZE, onPaginationChanged }
          : { onColumnMoved, onColumnVisible, onColumnPinned, onColumnResized, onSortChanged, onFilterChanged, rowClassRules })}
      />
      {pager}
    </div>
  );
}
